import { useCallback, useMemo } from 'react';
import {
    getGSLADataForMultipleWarehouses,
    getGSLARecommendationsForMultipleWarehouses,
} from 'src/core/hooks/useGranularSLA';
import { GSLADefinitionsByMutipleWarehouses, GSLARecommendationsByMutipleWarehouses } from 'src/features/types/gslaApi';
import { WarehouseGSLAEntry } from '../types/bulkGslaTypes';
import { WarehouseHandlingTier } from 'src/types';

export const useGSLAData = (siteIdList: string[], selectedHandlingTier?: string) => {
    const { data: gslaData, isLoading: isGSLADataLoading } = getGSLADataForMultipleWarehouses(siteIdList, true);
    const { data: gslaRecommendations, isLoading: isGSLARecommendationsLoading } = getGSLARecommendationsForMultipleWarehouses(siteIdList);

    const productionCapabilitySet = useMemo(() => {
        const pcSet = new Set<string>();

        if (gslaData) {
            Object.values(gslaData.warehouseToGSLAMap).forEach(gslaList => {
                gslaList.forEach(gsla => {
                    pcSet.add(gsla.productionCapability);
                });
            });
        }

        if (gslaRecommendations) {
            Object.values(gslaRecommendations.warehouseToGSLARecommendationMap).forEach(recList => {
                recList.forEach(rec => {
                    pcSet.add(rec.productionCapability);
                });
            });
        }

        return pcSet;
    }, [gslaData, gslaRecommendations]);

    const productionCapabilityArray = useMemo(() => 
        Array.from(productionCapabilitySet).sort(),
        [productionCapabilitySet]
    );

    const mergeGSLAData = useCallback((
        gslaData: GSLADefinitionsByMutipleWarehouses | undefined,
        gslaRecommendations: GSLARecommendationsByMutipleWarehouses | undefined,
    ): WarehouseGSLAEntry[] | undefined => {
        if (!gslaData || !gslaRecommendations) {
            return undefined;
        }

        const allWarehouseIds = new Set([
            ...Object.keys(gslaData.warehouseToGSLAMap),
            ...Object.keys(gslaRecommendations.warehouseToGSLARecommendationMap),
        ]);

        return Array.from(allWarehouseIds).map(warehouseId => {
            const warehouseEntry: WarehouseGSLAEntry = {
                warehouseId,
            };

            const actualGSLAs = gslaData.warehouseToGSLAMap[warehouseId] || [];
            const recommendedGSLAs = gslaRecommendations.warehouseToGSLARecommendationMap[warehouseId] || [];

            productionCapabilityArray.forEach(pc => {
                // Filter by selected handling tier
                const matchingGSLA = actualGSLAs.find(gsla => {
                    const matches = gsla.productionCapability === pc && 
                        (gsla.warehouseHandlingTier === selectedHandlingTier || 
                         (!gsla.warehouseHandlingTier && selectedHandlingTier === WarehouseHandlingTier.Default));
                    return matches;
                });

                warehouseEntry[pc] = {
                    actualGSLAInSeconds: matchingGSLA?.gslaInSeconds,
                    recommendedGSLAInSeconds: recommendedGSLAs.find(rec =>
                        rec.productionCapability === pc
                    )?.gslaInSeconds,
                };
            });

            return warehouseEntry;
        });
    }, [productionCapabilityArray, selectedHandlingTier]);

    const mergedData = mergeGSLAData(gslaData!, gslaRecommendations!);

    return {
        gslaData,
        gslaRecommendations,
        isGSLADataLoading,
        isGSLARecommendationsLoading,
        productionCapabilitySet,
        productionCapabilityArray,
        mergedData
    };
};
